import { initialize, type LDClient, type LDContext } from 'launchdarkly-js-client-sdk';
import { isEqual } from 'lodash-es';
import type { User, Me } from '@/types/users';
import type { Org } from '@/types/org';
import type { Team } from '@/types/teams';
import { getSportTypeKey } from '@/types/teams';
import { useGmApi } from '@/composables/useGmApi';
import { makeMeQueryKey } from '@/utils/queries';

let client: LDClient | undefined = undefined;
let currentContext: LDContext | undefined = undefined;

export interface BuildContextParams {
  user?: User | null;
  org?: Org | null;
  teams?: Team[] | null;
  teamTwrn?: string | null;
}

export function buildLDContext(params: BuildContextParams): LDContext {
  const { user, org, teams, teamTwrn } = params;

  const context: LDContext = {
    kind: 'multi',
  };

  if (user) {
    context.user = {
      key: user.twrn,
      name: user.fullName,
      email: user.email,
    };
  }

  if (org) {
    context.org = {
      key: org.twrn,
      name: org.name,
    };
  }

  if (teamTwrn && teams) {
    const currentTeam = teams.find((team) => team.twrn === teamTwrn);
    if (currentTeam) {
      context.team = {
        key: currentTeam.twrn,
        name: currentTeam.name,
        sport_type: getSportTypeKey(currentTeam.sportType),
      };
    }
  }

  return context;
}

async function getContextParams(): Promise<BuildContextParams | null> {
  const { useMainStore } = await import('@/stores/mainStore');
  const { queryClient } = await import('@/plugins/vueQuery');

  const mainStore = useMainStore();

  const me = await queryClient.fetchQuery<Me>({
    queryKey: makeMeQueryKey(),
    queryFn: useGmApi().getMe,
    staleTime: Infinity,
    retry: false,
  });

  return {
    user: me?.user,
    org: me?.org,
    teams: me?.teams,
    teamTwrn: mainStore.teamTwrn,
  };
}

export async function updateLDContext(): Promise<LDClient | undefined> {
  const clientSideID = import.meta.env.VITE_LAUNCH_DARKLY_CLIENT_ID;

  if (!clientSideID) {
    console.warn('[LaunchDarkly] No client ID configured');
    return undefined;
  }

  const params = await getContextParams();

  if (!params) {
    console.warn('[LaunchDarkly] User context missing');
    return undefined;
  }

  const newContext = buildLDContext(params);

  if (client && isEqual(currentContext, newContext)) {
    return client;
  }

  currentContext = newContext;

  if (client) {
    await client.identify(currentContext);
    return client;
  }

  client = initialize(clientSideID, currentContext, {
    sendEventsOnlyForVariation: true,
    streaming: true,
  });

  await client.waitForInitialization(5);

  return client;
}

export async function checkLDFlags(flagKeys: string[]): Promise<boolean> {
  const ldClient = await updateLDContext();
  if (!ldClient) return false;

  for (const flag of flagKeys) {
    if (ldClient.variation(flag, false) === false) {
      return false;
    }
  }
  return true;
}
